import type { ApiError, ErrorCode, FieldError, JsonObject } from '@cyphex/types';

/**
 * Thrown for every non-2xx response and for a dead network (status 0).
 * Components never render `message` — they show their own copy and use `code`.
 */
export class ApiRequestError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string;
  readonly retryAfter: number | null;
  /** Per-field problems on `validation_failed`; empty otherwise. */
  readonly fields: FieldError[];
  /** Machine-usable extras — e.g. the `challengeId` on `mfa_required`. */
  readonly details: JsonObject | null;

  constructor(status: number, envelope: ApiError['error']) {
    super(envelope.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = envelope.code;
    this.requestId = envelope.requestId;
    this.retryAfter = envelope.retryAfter ?? null;
    this.fields = envelope.fields ?? [];
    this.details = envelope.details ?? null;
  }

  /** Retrying a 4xx never helps except on 429; the chat outbox reads this. */
  get retryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }

  /** The session is gone — the auth feature listens for this to re-login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}
